import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReorderHubsPopup from '../ReorderHubsPopup';
import PublishLoopPopup from '../PublishPopup';
import LoopClonePopup from '../LoopClonePopup';
import InviteParticipantsPopup from '../../pages/MySessions/InviteParticipantsPopup';
import useApi from '../../hooks/useApi';
import Utils from '../../../app/assets/js/shared/utils';

import {
  ContentEditorSection,
  FieldSet,
  FieldSetLabel,
  LinkButton,
  ReviewPanel,
  ReviewForm,
  ReviewButton,
} from './styles';

const Options = ({
  options,
  fileService,
  flash,
  data,
  setCollaborators,
  collaborators,
  translations,
  isSession,
  loopSettings,
}) => {
  const { userToken, circle_uuid, circle_id, library_id, inReview } = options;
  const { allowedToCloneLoop, hasDocumentCategories, userCanPublish, isTeacher, isReviewer } = loopSettings;

  const { getData } = useApi(userToken);

  const [shareLink, setShareLink] = useState('');
  const [addCollaboratorPopupIsOpen, setAddCollaboratorPopupIsOpen] = useState(false);
  const [reorderHubsPopupIsOpen, setReorderHubsPopupIsOpen] = useState(false);
  const [publishLoopPopupIsOpen, setPublishLoopPopupIsOpen] = useState(false);
  const [cloneLoopPopupIsOpen, setCloneLoopPopupIsOpen] = useState(false);
  const [curriculumPopupIsOpen, setCurriculumPopupIsOpen] = useState(false);

  const invitedParticipantIds = collaborators.map((collaborator) => collaborator.id);

  useEffect(() => {
    document.addEventListener('hideCurriculumPopup', () => setCurriculumPopupIsOpen(false));
    return () => document.removeEventListener('hideCurriculumPopup', setCurriculumPopupIsOpen);
  }, []);

  useEffect(() => {
    if (curriculumPopupIsOpen) {
      document.dispatchEvent(new CustomEvent('showCurriculumPopup'));
    }
  }, [curriculumPopupIsOpen]);

  const addCollaborator = (newCollaborators = []) => {
    setAddCollaboratorPopupIsOpen(false);
    const updatedCollaborators = [...collaborators];
    newCollaborators
      .filter((collaborator) => !invitedParticipantIds.includes(collaborator.id))
      .forEach((collaborator) => {
        const { userId, firstName, lastName, image } = collaborator;
        updatedCollaborators.push({
          ...collaborator,
          User: { id: userId, image, full_name: `${firstName} ${lastName}` },
        });
      });
    setCollaborators(updatedCollaborators);
  };

  const circleTexts = translations.circle;
  const appTexts = translations.app;
  const actions = circleTexts.action;

  const getShareLink = async () => {
    const response = await getData(`/my-loops/${circle_id}/share_link`);
    if (response.success) {
      setShareLink(response.body.share_link);
    }
  };

  const copyShareLink = () => {
    Utils.copyToClipboard(shareLink);
    flash(i18n.__('link_has_been_copied_to_clipboard'), { type: 'status' });
  };

  return (
    <ContentEditorSection>
      <FieldSet>
        <FieldSetLabel>{appTexts.options}</FieldSetLabel>
        {inReview && isReviewer && (
          <ReviewPanel>
            <ReviewForm method="POST" action={`/my-loops/${circle_id}/review/${library_id}?_method=PUT`}>
              <ReviewButton type="submit" name="state" value="reject">
                {appTexts.reject}
              </ReviewButton>
              <ReviewButton type="submit" name="state" value="accept">
                {appTexts.accept}
              </ReviewButton>
            </ReviewForm>
          </ReviewPanel>
        )}
        {!isSession && (
          <LinkButton
            className="share-link"
            title={shareLink ? i18n.__('app_copy_to_clipboard') : ''}
            onClick={shareLink ? copyShareLink : getShareLink}
          >
            {shareLink ? shareLink : translations.share_link.share_link}
          </LinkButton>
        )}
        {hasDocumentCategories && (
          <LinkButton className="curriculum-link" onClick={() => setCurriculumPopupIsOpen(true)}>
            {circleTexts.curriculum.edit}
          </LinkButton>
        )}
        {!isSession && !inReview && (
          <LinkButton className="add-collaborator" onClick={() => setAddCollaboratorPopupIsOpen(true)}>
            {actions.add_collaborator}
          </LinkButton>
        )}
        {userCanPublish && isTeacher && (
          <LinkButton className="library-publish" onClick={() => setPublishLoopPopupIsOpen(true)}>
            {actions.publish}
          </LinkButton>
        )}
        {isTeacher && (
          <LinkButton className="reorder-hubs" onClick={() => setReorderHubsPopupIsOpen(true)}>
            {circleTexts.reorder_hubs}
          </LinkButton>
        )}
        {allowedToCloneLoop && (
          <LinkButton className="copy-circle" onClick={() => setCloneLoopPopupIsOpen(true)}>
            {isSession ? actions.clone : actions.clone_template}
          </LinkButton>
        )}
      </FieldSet>
      <InviteParticipantsPopup
        userToken={userToken}
        loopId={circle_id}
        isTeachers={true}
        invitedParticipantIds={invitedParticipantIds}
        isNew={false}
        popupIsOpen={addCollaboratorPopupIsOpen}
        onClose={addCollaborator}
        flash={flash}
        showInvitationEmail={false}
        reload={false}
      />
      <PublishLoopPopup
        onClose={() => setPublishLoopPopupIsOpen(false)}
        popupIsOpen={publishLoopPopupIsOpen}
        flash={flash}
        fileService={fileService}
        userToken={userToken}
        entityType="loop"
        entityId={circle_id}
      />
      <ReorderHubsPopup
        loopId={circle_uuid}
        fileService={fileService}
        userToken={userToken}
        popupIsOpen={reorderHubsPopupIsOpen}
        onClose={(ev, startNodeId) => {
          if (startNodeId) {
            $('body').trigger('circle.updated', {
              startNodeId,
            });
          }
          setReorderHubsPopupIsOpen(false);
        }}
        flash={flash}
      />
      <LoopClonePopup
        popupIsOpen={cloneLoopPopupIsOpen}
        flash={flash}
        onClose={() => setCloneLoopPopupIsOpen(false)}
        onSuccess={({ id }) => {
          setCloneLoopPopupIsOpen(false);
          window.location.replace(`/editor/${id}`);
        }}
        userToken={userToken}
        loopId={circle_id}
        defaultName={data.name}
        isSession={isSession}
      />
    </ContentEditorSection>
  );
};

export default Options;

Options.propTypes = {
  options: PropTypes.object.isRequired,
  fileService: PropTypes.object.isRequired,
  translations: PropTypes.object.isRequired,
  flash: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
  setCollaborators: PropTypes.func.isRequired,
  collaborators: PropTypes.array.isRequired,
  loopSettings: PropTypes.object.isRequired,
  isSession: PropTypes.bool.isRequired,
};
